import { useDispatch, useSelector } from 'react-redux';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { selectActiveTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { CancelDialog } from '../CancelDialog';
import { AttachmentCard } from '../AttachmentCard';
import {
    createMedia,
    updateMedia,
    fetchMediaByNode,
    getContentMediaByNodeId,
    deleteMedia,
} from 'GeminiViewerComponent/features/globals/mediaSlice';
import { getProcedureSession } from 'GeminiViewerComponent/features/procedureSession/procedureSessionSlice';
import { activateLoading, deactivateLoading } from 'GeminiViewerComponent/features/globals/loadingProgressSlice';
import { dialogStyles } from 'GeminiViewerComponent/components/Procedure/styles';

const PreviewDialog = ({
    var_name,
    level,
    selectedImages,
    setSelectedImages,
    originalAttachmentData,
    openPreviewDialog,
    setOpenPreviewDialog,
    openFileSelection,
}) => {
    const dispatch = useDispatch();
    const [openCancelDialog, setOpenCancelDialog] = useState(false);
    const theme = useSelector(selectActiveTheme);
    const classes = dialogStyles(theme);
    const procedure = useSelector((state) => state.procedure);
    const contentMediaByNodeId = useSelector(getContentMediaByNodeId);
    const procedureSessionData = useSelector(getProcedureSession);

    const treeId = procedure?.active_tree_id;
    const nodeSelected = treeId != null ? procedure?.trees?.[String(treeId)]?.current_node : undefined;

    const nodeStoreMedia = useMemo(() => {
        const nodeId = nodeSelected?.id;
        if (!nodeId) return [];
        return contentMediaByNodeId?.[String(nodeId)] ?? [];
    }, [contentMediaByNodeId, nodeSelected?.id]);

    const procedureSessionId = useMemo(() => {
        return procedureSessionData?.procedure_session_id;
    }, [procedureSessionData?.procedure_session_id]);

    const mapStoreMediaToSelectedImages = useCallback((mediaList) => {
        return (mediaList ?? [])
            .filter((m) => (m?.mediaType ?? '').toLowerCase() === 'image' && !!m?.url)
            .map((m) => ({
                name: m.displayName,
                url: m.url,
                media_id: m.mediaId,
                isSaved: true,
                file: null,
                fileSize: m.fileSize,
                fileWidth: m.fileWidth,
                fileHeight: m.fileHeight,
                toDelete: false,
            }));
    }, []);

    useEffect(() => {
        if (!openPreviewDialog) return;
        if (!nodeSelected?.id) return;
        if (selectedImages?.length > 0) return;

        const mapped = mapStoreMediaToSelectedImages(nodeStoreMedia);
        setSelectedImages(mapped);
    }, [
        openPreviewDialog,
        nodeSelected?.id,
        nodeStoreMedia,
        mapStoreMediaToSelectedImages,
        setSelectedImages,
        selectedImages?.length,
    ]);

    const handleSubmit = useCallback(async () => {
        dispatch(activateLoading());
        let isError = false;

        const all = selectedImages ?? [];
        const imagesToDelete = all.filter((img) => img?.toDelete === true && img?.media_id != null);
        const imagesToKeep = all.filter((img) => img?.toDelete !== true);

        for (const img of imagesToDelete) {
            const res = await dispatch(deleteMedia(Number(img.media_id)));
            if (!deleteMedia.fulfilled.match(res)) {
                isError = true;
            }
        }

        const updatedImages = [];
        for (let i = 0; i < imagesToKeep.length; i++) {
            const imgObj = imagesToKeep[i];

            if (imgObj?.isSaved !== true) {
                let response = null;

                if (imgObj?.file) {
                    response = await dispatch(
                        createMedia({
                            display_name: imgObj?.name,
                            media_data: imgObj?.file,
                            draft: false,
                            procedure_id: treeId,
                            node_id: nodeSelected?.id,
                            procedure_session_id: procedureSessionId,
                        })
                    );
                } else {
                    response = await dispatch(
                        updateMedia({
                            display_name: imgObj?.name,
                            media_id: imgObj?.media_id,
                            procedure_id: treeId,
                            node_id: nodeSelected?.id,
                            procedure_session_id: procedureSessionId,
                        })
                    );
                }

                if (!response.error) {
                    updatedImages.push({
                        ...imgObj,
                        url: response.payload?.url ?? imgObj.url,
                        isSaved: true,
                        file: null,
                        media_id: response.payload?.media_id ?? imgObj.media_id,
                        toDelete: false,
                    });
                } else {
                    updatedImages.push(imgObj);
                    isError = true;
                }
            } else {
                updatedImages.push(imgObj);
            }
        }

        setSelectedImages(updatedImages);

        if (isError === false) {
            if (treeId && nodeSelected?.id) {
                await dispatch(
                    fetchMediaByNode({ procedure_id: treeId, node_id: nodeSelected.id, procedureSessionId })
                );
            }
            setOpenPreviewDialog(false);
        }

        dispatch(deactivateLoading());
    }, [
        dispatch,
        treeId,
        nodeSelected?.id,
        selectedImages,
        setSelectedImages,
        setOpenPreviewDialog,
        procedureSessionId,
    ]);

    return (
        <>
            <Dialog fullWidth open={openPreviewDialog} onClose={() => setOpenCancelDialog(true)} maxWidth={'lg'}>
                <DialogTitle display={'flex'} justifyContent={'center'}>
                    <Button onClick={openFileSelection} variant="outlined">
                        Add Images
                    </Button>
                </DialogTitle>

                <DialogContent>
                    <DialogContentText
                        margin={'15px'}
                        display={'flex'}
                        flexDirection={'row'}
                        gap={'10px'}
                        flexWrap={'wrap'}
                        justifyContent={'center'}
                    >
                        {selectedImages?.length > 0 &&
                            selectedImages.map((image, idx) => (
                                <AttachmentCard
                                    key={`attachment-card-${idx}`}
                                    attachImage={image}
                                    index={idx}
                                    var_name={var_name}
                                    setSelectedImages={setSelectedImages}
                                />
                            ))}
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button className={classes.cancel} onClick={() => setOpenCancelDialog(true)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} className={classes.submit}>
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>

            <CancelDialog
                originalAttachmentData={originalAttachmentData}
                setSelectedImages={setSelectedImages}
                setOpenPreviewDialog={setOpenPreviewDialog}
                openCancelDialog={openCancelDialog}
                setOpenCancelDialog={setOpenCancelDialog}
            />
        </>
    );
};

export { PreviewDialog };
